function normalizeStepName(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function matchesStep(stepDef, historyName) {
  const normalized = normalizeStepName(historyName);
  if (!normalized) return false;
  const candidates = [stepDef.label, ...(stepDef.aliases || [])];
  return candidates.some((name) => normalizeStepName(name) === normalized);
}

function findStepDef(stepName, steps = []) {
  return steps.find((stepDef) => matchesStep(stepDef, stepName)) || null;
}

function matchesExcludedStep(historyStepName, stepDef, excludeLabels = []) {
  if (!excludeLabels.length) return false;

  return excludeLabels.some((excluded) => {
    const normalizedExcluded = normalizeStepName(excluded);
    if (!normalizedExcluded) return false;

    if (normalizeStepName(historyStepName) === normalizedExcluded) return true;
    if (stepDef?.label && normalizeStepName(stepDef.label) === normalizedExcluded) {
      return true;
    }

    return (stepDef?.aliases || []).some(
      (alias) => normalizeStepName(alias) === normalizedExcluded,
    );
  });
}

function toMs(value) {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? null : ms;
}

function formatDayName(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", { weekday: "long" });
}

export function formatStepDuration(totalMs) {
  if (totalMs == null || Number.isNaN(totalMs) || totalMs < 0) return "-";

  let remaining = Math.floor(totalMs / 1000);
  const days = Math.floor(remaining / 86400);
  remaining %= 86400;
  const hours = Math.floor(remaining / 3600);
  remaining %= 3600;
  const minutes = Math.floor(remaining / 60);
  const seconds = remaining % 60;

  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0 || days > 0) parts.push(`${hours}h`);
  if (minutes > 0 || hours > 0 || days > 0) parts.push(`${minutes}m`);
  parts.push(`${seconds}s`);

  return parts.join(" ");
}

export function formatSlaLabel(days) {
  if (days == null || days <= 0) return "0 Min";
  if (days === 1) return "1 Day";
  return `${days} Days`;
}

function slaToMs(days) {
  if (!days || days <= 0) return null;
  return days * 24 * 60 * 60 * 1000;
}

function formatProgressDisplay(rawPct) {
  if (rawPct == null || Number.isNaN(rawPct)) return null;
  if (rawPct > 0 && rawPct < 1) return "<1%";
  return `${Math.round(rawPct)}%`;
}

function getProgressBarWidth(rawPct, status) {
  if (rawPct == null || Number.isNaN(rawPct)) return 0;
  if (status === "delayed") return 100;
  if (rawPct > 0 && rawPct < 1) return 1;
  return Math.min(Math.max(rawPct, 0), 100);
}

function getProgressMeta(durationMs, slaDays) {
  const slaMs = slaToMs(slaDays);
  if (durationMs == null || !slaMs) {
    return {
      hasProgress: false,
      progressPct: null,
      progressRawPct: null,
      progressLabel: null,
      progressBarWidth: 0,
      status: "on-time",
      overdueMs: 0,
    };
  }

  const progressRawPct = (durationMs / slaMs) * 100;
  const progressPct = Math.round(progressRawPct);
  let status = "on-time";
  if (progressRawPct > 100) status = "delayed";
  else if (progressRawPct > 80) status = "near-sla";

  const overdueMs = Math.max(0, durationMs - slaMs);

  return {
    hasProgress: true,
    progressPct,
    progressRawPct,
    progressLabel: formatProgressDisplay(progressRawPct),
    progressBarWidth: getProgressBarWidth(progressRawPct, status),
    status,
    overdueMs,
  };
}

function findStepDefByProgressId(progressId, steps = []) {
  const stepNo = Number(progressId);
  if (!Number.isFinite(stepNo)) return null;
  return steps.find((stepDef) => stepDef.id === stepNo + 1) || null;
}

function getHistoryProgressId(item) {
  if (!item) return null;
  const progress = item.historyProgressId ?? item.applicationProgressId;
  if (progress == null || progress === "") return null;
  const parsed = Number(progress);
  return Number.isFinite(parsed) ? parsed : null;
}

function isObjectedStatus(status) {
  return Number(status) === 1;
}

function pickCurrentStepActor(currentStatus = {}) {
  return (
    currentStatus.lastModifiedBy ||
    currentStatus.userId ||
    currentStatus.userName ||
    ""
  );
}

function pickCurrentStepRemarks(currentStatus = {}) {
  const remarks = String(currentStatus.remarks || "").trim();
  if (remarks) return remarks;
  const detail = String(currentStatus.remarksDetail || "").trim();
  return detail || "-";
}

function buildCurrentStepDetailRow({
  historyItems,
  currentStatus,
  steps,
  stepNameField,
  totalExcludeStepLabels,
  currentProgressId,
  formatDateFn,
  creationMs,
  findStepDefByProgressIdFn,
  resolveCurrentStepName,
  shouldAdvancePastHistoryMatch,
}) {
  const progressId = Number(currentProgressId);
  if (!Number.isFinite(progressId) || progressId < 0) {
    return null;
  }

  const formatDate =
    typeof formatDateFn === "function"
      ? formatDateFn
      : (value) => (value ? String(value) : "-");

  const newestHistory = historyItems[0] ?? null;
  const newestProgressId = getHistoryProgressId(newestHistory);
  const lastStepDef = steps[steps.length - 1];
  const advancePast = shouldAdvancePastHistoryMatch?.(newestHistory, progressId);

  if (
    !advancePast &&
    lastStepDef &&
    newestProgressId != null &&
    newestProgressId >= lastStepDef.id - 1 &&
    newestProgressId === progressId
  ) {
    return null;
  }

  const resolveStepDef =
    typeof findStepDefByProgressIdFn === "function"
      ? findStepDefByProgressIdFn
      : findStepDefByProgressId;

  const stepDef =
    resolveStepDef(progressId, steps, currentStatus, historyItems) ||
    findStepDef(currentStatus?.[stepNameField], steps);
  const stepNo = stepDef?.id ?? progressId + 1;
  const stepName =
    resolveCurrentStepName?.(progressId, currentStatus, steps, historyItems) ||
    currentStatus?.[stepNameField] ||
    stepDef?.label ||
    "-";
  const slaDays = stepDef?.slaDays ?? 1;
  const isDocumentSubmission = stepDef?.label === "Document Submission";
  const excludeFromTotal =
    matchesExcludedStep(stepName, stepDef, totalExcludeStepLabels) ||
    isDocumentSubmission;

  const previousHistoryItem = advancePast
    ? newestHistory
    : newestProgressId === progressId
      ? historyItems[1]
      : newestHistory;
  let previousMs = toMs(previousHistoryItem?.lastModificationDate);
  if (previousMs == null) {
    previousMs = creationMs;
  }

  const now = new Date();
  const durationMs =
    previousMs != null ? Math.max(0, now.getTime() - previousMs) : null;

  const {
    hasProgress,
    progressPct,
    progressLabel,
    progressBarWidth,
    status,
    overdueMs,
  } = getProgressMeta(
    isDocumentSubmission ? null : durationMs,
    isDocumentSubmission ? null : slaDays,
  );

  const isObjectedCurrentStep = isObjectedStatus(currentStatus?.objectionStatus);
  const processedBy = isObjectedCurrentStep
    ? pickCurrentStepActor(currentStatus) || "-"
    : "-";
  const remarks = isObjectedCurrentStep
    ? pickCurrentStepRemarks(currentStatus)
    : "In progress.";

  return {
    stepNo,
    stepName,
    processedBy,
    dateTime: formatDate(now),
    dateTimeDay: formatDayName(now),
    durationLabel:
      durationMs != null ? formatStepDuration(durationMs) : "-",
    durationMs,
    slaLabel: isDocumentSubmission ? "-" : formatSlaLabel(slaDays),
    slaDays,
    hasProgress: isDocumentSubmission ? false : hasProgress,
    progressPct: isDocumentSubmission ? null : progressPct,
    progressLabel: isDocumentSubmission ? "-" : progressLabel,
    progressBarWidth: isDocumentSubmission ? 0 : progressBarWidth,
    status: isDocumentSubmission ? null : status,
    overdueLabel:
      !isDocumentSubmission && status === "delayed" && overdueMs > 0
        ? formatStepDuration(overdueMs)
        : "",
    remarks,
    isPending: false,
    isCurrentStep: true,
    isObjectedCurrentStep,
    isDocumentSubmission,
    excludeFromTotal,
    _raw: null,
  };
}

function applyCurrentStepToDetailRows(
  rows,
  historyItems,
  currentStatus,
  options,
  formatDateFn,
  creationMs,
) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return rows;
  }

  const currentProgressId =
    options.currentProgressId ?? currentStatus.applicationProgressId;
  if (currentProgressId == null || currentProgressId === "") {
    return rows;
  }

  const newestProgressId = getHistoryProgressId(historyItems[0]);
  const progressId = Number(currentProgressId);
  if (!Number.isFinite(progressId)) {
    return rows;
  }

  if (newestProgressId === progressId) {
    if (options.shouldAdvancePastHistoryMatch?.(historyItems[0], progressId)) {
      const currentRow = buildCurrentStepDetailRow({
        historyItems,
        currentStatus,
        steps: options.steps,
        stepNameField: options.stepNameField,
        totalExcludeStepLabels: options.totalExcludeStepLabels,
        currentProgressId,
        formatDateFn,
        creationMs,
        findStepDefByProgressIdFn: options.findStepDefByProgressIdFn,
        resolveCurrentStepName: options.resolveCurrentStepName,
        shouldAdvancePastHistoryMatch: options.shouldAdvancePastHistoryMatch,
      });
      return currentRow ? [currentRow, ...rows] : rows;
    }

    const currentRow = buildCurrentStepDetailRow({
      historyItems,
      currentStatus,
      steps: options.steps,
      stepNameField: options.stepNameField,
      totalExcludeStepLabels: options.totalExcludeStepLabels,
      currentProgressId,
      formatDateFn,
      creationMs,
      findStepDefByProgressIdFn: options.findStepDefByProgressIdFn,
      resolveCurrentStepName: options.resolveCurrentStepName,
      shouldAdvancePastHistoryMatch: options.shouldAdvancePastHistoryMatch,
    });
    if (!currentRow) {
      return rows;
    }

    return [{ ...rows[0], ...currentRow, _raw: null }, ...rows.slice(1)];
  }

  if (newestProgressId != null && progressId < newestProgressId) {
    return rows;
  }

  const currentRow = buildCurrentStepDetailRow({
    historyItems,
    currentStatus,
    steps: options.steps,
    stepNameField: options.stepNameField,
    totalExcludeStepLabels: options.totalExcludeStepLabels,
    currentProgressId,
    formatDateFn,
    creationMs,
    findStepDefByProgressIdFn: options.findStepDefByProgressIdFn,
    resolveCurrentStepName: options.resolveCurrentStepName,
    shouldAdvancePastHistoryMatch: options.shouldAdvancePastHistoryMatch,
  });

  return currentRow ? [currentRow, ...rows] : rows;
}

/**
 * Build detail rows from API history only (same rows as main Activity Log).
 * historyItems must already be sorted DESC (newest first).
 */
export function buildActivityLogDetailRows(
  historyItems = [],
  currentStatus = {},
  formatDateFn,
  {
    steps = [],
    stepNameField = "reConProgStatusName",
    totalExcludeStepLabels = [],
    currentProgressId,
    findStepDefByProgressIdFn,
    resolveCurrentStepName,
    shouldAdvancePastHistoryMatch,
  } = {},
) {
  if (!Array.isArray(historyItems) || historyItems.length === 0) {
    return [];
  }

  const creationMs = toMs(currentStatus.creationDate);
  const formatDate =
    typeof formatDateFn === "function"
      ? formatDateFn
      : (value) => (value ? String(value) : "-");

  const options = {
    steps,
    stepNameField,
    totalExcludeStepLabels,
    currentProgressId,
    findStepDefByProgressIdFn,
    resolveCurrentStepName,
    shouldAdvancePastHistoryMatch,
  };

  const total = historyItems.length;

  const rows = historyItems.map((item, index) => {
    const isOldest = index === total - 1;
    const stepMs = toMs(item.lastModificationDate);
    const previousItem = isOldest ? null : historyItems[index + 1];
    const historyStepName = item[stepNameField];
    const stepDef = findStepDef(historyStepName, steps);
    const slaDays = stepDef?.slaDays ?? 1;
    const isDocumentSubmission =
      isOldest && stepDef?.label === "Document Submission";
    const excludeFromTotal =
      matchesExcludedStep(
        historyStepName,
        stepDef,
        totalExcludeStepLabels,
      ) || stepDef?.label === "Document Submission";

    let durationMs = null;
    if (!isOldest && stepMs != null) {
      let prevMs = toMs(previousItem?.lastModificationDate);
      if (prevMs == null) {
        prevMs = creationMs;
      }
      if (prevMs != null) {
        durationMs = Math.max(0, stepMs - prevMs);
      }
    }

    const displayDate =
      stepMs != null
        ? item.lastModificationDate
        : isOldest
          ? currentStatus.creationDate
          : null;

    const processedBy =
      item.lastModifiedBy || (isOldest ? currentStatus.createdBy : null);

    const remarks = isOldest
      ? item.remarks || "Application Submitted."
      : item.remarks || "-";

    const {
      hasProgress,
      progressPct,
      progressLabel,
      progressBarWidth,
      status,
      overdueMs,
    } = getProgressMeta(
      isOldest ? null : durationMs,
      isDocumentSubmission ? null : slaDays,
    );

    const stepNo =
      item.historyProgressId != null
        ? Number(item.historyProgressId) + 1
        : stepDef?.id ?? index + 1;

    return {
      stepNo,
      stepName: historyStepName || stepDef?.label || "-",
      processedBy: processedBy || "-",
      dateTime: displayDate ? formatDate(displayDate) : "-",
      dateTimeDay: displayDate ? formatDayName(displayDate) : null,
      durationLabel:
        isOldest
          ? "-"
          : durationMs != null
            ? formatStepDuration(durationMs)
            : "-",
      durationMs: isOldest ? null : durationMs,
      slaLabel: isDocumentSubmission ? "-" : formatSlaLabel(slaDays),
      slaDays,
      hasProgress: isDocumentSubmission ? false : hasProgress,
      progressPct: isDocumentSubmission ? null : progressPct,
      progressLabel: isDocumentSubmission ? "-" : progressLabel,
      progressBarWidth: isDocumentSubmission ? 0 : progressBarWidth,
      status: isDocumentSubmission ? null : status,
      overdueLabel:
        !isDocumentSubmission &&
        status === "delayed" &&
        overdueMs > 0
          ? formatStepDuration(overdueMs)
          : "",
      remarks,
      isPending: false,
      isDocumentSubmission,
      excludeFromTotal,
      _raw: item,
    };
  });

  return applyCurrentStepToDetailRows(
    rows,
    historyItems,
    currentStatus,
    options,
    formatDateFn,
    creationMs,
  );
}

export function buildActivityLogDetailTotalRow(rows = [], formatDateFn) {
  if (!Array.isArray(rows) || rows.length === 0) return null;

  const now = new Date();
  const formatDate =
    typeof formatDateFn === "function"
      ? formatDateFn
      : (value) => (value ? String(value) : "-");

  let totalDurationMs = 0;
  let totalSlaDays = 0;

  rows.forEach((row) => {
    if (row.isDocumentSubmission || row.excludeFromTotal) return;
    if (row.durationMs != null) {
      totalDurationMs += row.durationMs;
    }
    if (row.slaDays != null && row.slaDays > 0) {
      totalSlaDays += row.slaDays;
    }
  });

  const {
    hasProgress,
    progressLabel,
    progressBarWidth,
    status,
    overdueMs,
  } = getProgressMeta(totalDurationMs, totalSlaDays);

  return {
    isTotalRow: true,
    stepNo: null,
    stepName: "Total",
    processedBy: "SYSTEM",
    dateTime: formatDate(now),
    dateTimeDay: formatDayName(now),
    durationLabel:
      totalDurationMs > 0 ? formatStepDuration(totalDurationMs) : "-",
    durationMs: totalDurationMs,
    slaLabel: formatSlaLabel(totalSlaDays),
    slaDays: totalSlaDays,
    hasProgress,
    progressLabel,
    progressBarWidth,
    status,
    overdueLabel:
      status === "delayed" && overdueMs > 0
        ? formatStepDuration(overdueMs)
        : "",
    remarks: "Calculated by system.",
    isPending: false,
    isDocumentSubmission: false,
    _raw: null,
  };
}
